/**
 * Header V2 - Transform-Based Scroll Behavior
 *
 * Modern approach using fixed position with transform animations
 * Best practices 2024/2025
 *
 * States:
 * - Default: translateY(0) with glassmorphism styling (at top)
 * - Hidden: translateY(-100%) when scrolling down
 * - Scrolled: translateY(0) with white background when scrolling up
 */

class HeaderV2 {
  static instance = null;

  /**
   * Get or create singleton instance
   * Destroys previous instance if exists
   */
  static getInstance() {
    if (HeaderV2.instance) {
      HeaderV2.instance.destroy();
    }
    HeaderV2.instance = new HeaderV2();
    return HeaderV2.instance;
  }

  constructor() {
    this.header = document.querySelector('.header-v2');
    this.wrapper = document.querySelector('.header-v2-wrapper');

    if (!this.header || !this.wrapper) return;

    // Configuration
    this.ANNOUNCEMENT_BAR_HEIGHT = 40;
    this.SCROLL_THRESHOLD = 100; // Minimum scroll before header can be hidden

    // State tracking
    this.lastScrollY = window.scrollY;
    this.isFixed = false;   // Is header in fixed position?
    this.isHidden = false;  // Is header hidden (translateY -100%)?
    this.isScrolled = false; // Is header in scrolled style (white bg)?
    this.ticking = false;

    // Bind event handlers for proper removal
    this.scrollHandler = () => this.requestTick();
    this.resizeHandler = () => this.updateWrapperHeight();

    this.init();
  }

  init() {
    // Set wrapper height to prevent layout shift
    this.updateWrapperHeight();

    // Scroll listener with RequestAnimationFrame for performance
    window.addEventListener('scroll', this.scrollHandler, { passive: true });

    // Update wrapper height on resize
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    // Initial state check
    this.handleScroll();
  }

  /**
   * Cleanup method to remove event listeners
   * Called when destroying instance
   */
  destroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    // Reset static instance reference
    HeaderV2.instance = null;
  }

  /**
   * Request animation frame for optimized scroll handling
   */
  requestTick() {
    if (!this.ticking) {
      requestAnimationFrame(() => this.handleScroll());
      this.ticking = true;
    }
  }

  /**
   * Update wrapper min-height to match header height
   * Prevents content jump when header is fixed
   */
  updateWrapperHeight() {
    const headerHeight = this.header.offsetHeight;
    this.wrapper.style.minHeight = `${headerHeight}px`;
  }

  /**
   * Main scroll handler
   * Determines header state based on scroll position and direction
   */
  handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollingUp = currentScrollY < this.lastScrollY;
    const scrollingDown = currentScrollY > this.lastScrollY;

    // Zone 1: At the very top (0-40px) - Default relative header, scrolls naturally
    if (currentScrollY <= this.ANNOUNCEMENT_BAR_HEIGHT) {
      this.setRelative();
      this.setDefaultStyle();
    }

    // Zone 2: Scrolled past threshold - Fixed header, hide/show based on direction
    else if (currentScrollY > this.SCROLL_THRESHOLD) {
      this.setFixed(); // Always make it fixed once past threshold

      if (scrollingDown) {
        // Scrolling down: Hide header
        this.hideHeader();
      } else if (scrollingUp) {
        // Scrolling up: Show header with scrolled styling
        this.showHeader();
        this.setScrolledStyle();
      }
    }

    // Zone 3: Between announcement bar and threshold
    else {
      this.setFixed(); // Make it fixed
      if (scrollingDown) {
        this.hideHeader();
      }
    }

    this.lastScrollY = currentScrollY <= 0 ? 0 : currentScrollY; // Prevent negative values
    this.ticking = false;
  }

  /**
   * Set header to fixed position
   */
  setFixed() {
    if (this.isFixed) return;

    this.header.classList.add('header-v2--fixed');
    this.isFixed = true;
  }

  /**
   * Set header to relative position (default)
   */
  setRelative() {
    if (!this.isFixed) return;

    this.header.classList.remove('header-v2--fixed', 'header-v2--hidden');
    this.isFixed = false;
    this.isHidden = false;
  }

  /**
   * Show header (remove hidden state)
   * Sets transform to translateY(0)
   */
  showHeader() {
    if (!this.isHidden) return;

    this.header.classList.remove('header-v2--hidden');
    this.isHidden = false;
  }

  /**
   * Hide header (add hidden state)
   * Sets transform to translateY(-100%)
   */
  hideHeader() {
    if (this.isHidden) return;

    this.header.classList.add('header-v2--hidden');
    this.isHidden = true;
  }

  /**
   * Set default styling (glassmorphism, light icons)
   * Used when at the top of the page
   */
  setDefaultStyle() {
    if (!this.isScrolled) return;

    this.header.classList.remove('header-v2--scrolled');
    this.isScrolled = false;
  }

  /**
   * Set scrolled styling (white background, dark icons)
   * Used when header is revealed while scrolling up
   */
  setScrolledStyle() {
    if (this.isScrolled) return;

    this.header.classList.add('header-v2--scrolled');
    this.isScrolled = true;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => HeaderV2.getInstance());
} else {
  HeaderV2.getInstance();
}

// Shopify theme editor support - properly cleanup and reinitialize
document.addEventListener('shopify:section:load', () => HeaderV2.getInstance());
